import { useState, useEffect, useMemo, ReactNode, ChangeEvent } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  getPaginationRowModel,
} from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  ArrowUpDown,
  ArrowDown,
  ArrowUp,
  Loader2,
  Filter,
  RefreshCcw, // ✅ refresh icon
} from 'lucide-react';

// Types
export interface CommonTableProps<T extends object> {
  columns: ColumnDef<T, any>[];
  data: T[];
  isLoading?: boolean;
  page: number;
  pageSize: number;
  total: number;
  onResetSearch?: () => void; // ✅ new
  searchValue?: string;
  onSearchSubmit?: (query: string) => void;
  isSearchDisabled?: boolean;
  searchError?: string | null;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSortChange: (columnId: string, order: 'asc' | 'desc') => void;
  onSearchChange?: (query: string) => void;
  enableRowClick?: boolean;
  onRowClick?: (row: T) => void;
  actions?: {
    label: string;
    icon?: ReactNode;
    onClick: (row: T) => void;
    color?: string;
  }[];
  onRefresh?: () => void; // ✅ new
  searchPlaceholder?: string;
  pageSizeOptions?: number[];
  showPagination?: boolean;
  className?: string;
  emptyStateMessage?: string;
  title?: string;
  isProductTab?: boolean;
}

/**
 * CommonTable - A reusable table component with server-side pagination, sorting, filtering, search, and refresh
 */
export const CommonTable = <T extends object>({
  columns,
  data,
  isLoading = false,
  page,
  pageSize,
  total,
  onPageChange,
  isProductTab,
  onPageSizeChange,
  searchError,
  onResetSearch,
  searchValue,
  isSearchDisabled,
  onSortChange,
  onSearchChange,
  onSearchSubmit,
  enableRowClick = false,
  onRowClick,
  actions = [],
  onRefresh,
  searchPlaceholder = 'Search users...',
  pageSizeOptions = [10, 25, 50, 100],
  showPagination = true,
  className = '',
  emptyStateMessage = 'No data found',
  title,
}: CommonTableProps<T>) => {
  // State
  // const [searchQuery, setSearchQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  // const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [prevSorting, setPrevSorting] = useState<SortingState>([]);
  const [isRefreshing, setIsRefreshing] = useState(false); // ✅
  const [refreshDisabled, setRefreshDisabled] = useState(false); // ✅

  // Calculate total pages
  const totalPages = Math.ceil(total / pageSize);

  // Add actions column if actions are provided
  const memoizedColumns = useMemo(() => {
    if (actions && actions.length > 0) {
      return [
        ...columns,
        {
          id: 'actions',
          header: 'Actions',
          cell: ({ row }) => (
            <div className="flex items-center space-x-2">
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick(row.original);
                  }}
                  className={`px-3 py-1.5 rounded-md text-sm font-gilroy-medium flex items-center ${action.color
                    ? `text-${action.color}-600 hover:bg-${action.color}-50`
                    : 'text-blue-600 hover:bg-blue-50'
                    }`}
                  title={action.label}
                >
                  {action.icon && <span className="mr-1.5">{action.icon}</span>}
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          ),
        },
      ] as ColumnDef<T, any>[];
    }
    return columns;
  }, [columns, actions]);

  // Initialize table
  const table = useReactTable({
    data,
    columns: memoizedColumns,
    state: { sorting },
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Watch for sorting changes
  useEffect(() => {
    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      let hasChanged = false;
      if (prevSorting.length === 0) {
        hasChanged = true;
      } else {
        const { id: prevId, desc: prevDesc } = prevSorting[0];
        hasChanged = id !== prevId || desc !== prevDesc;
      }
      if (hasChanged) {
        onSortChange(id, desc ? 'desc' : 'asc');
        setPrevSorting(sorting);
      }
    }
  }, [sorting, prevSorting, onSortChange]);

  // Debounce search query
  // useEffect(() => {
  //   const timerId = setTimeout(() => {
  //     setDebouncedSearchQuery(searchQuery);
  //   }, 300);
  //   return () => clearTimeout(timerId);
  // }, [searchQuery]);

  // // Handle search query change
  // useEffect(() => {
  //   if (onSearchChange && debouncedSearchQuery !== undefined) {
  //     onSearchChange(debouncedSearchQuery);
  //   }
  // }, [debouncedSearchQuery, onSearchChange]);

  // Handle search input change
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    onSearchChange?.(e.target.value); // validation / parent updates state
  };

  // Handle page size change
  const handlePageSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = Number(e.target.value);
    onPageSizeChange(newPageSize);
  };

  // Handle refresh click
  // Handle refresh click
  const handleRefresh = () => {
    if (!refreshDisabled && onRefresh) {
      setIsRefreshing(true);

      // ✅ Reset search input in parent
      onResetSearch?.();

      onRefresh();
      setRefreshDisabled(true);
      setTimeout(() => setRefreshDisabled(false), 60000); // disable 1 min
      setTimeout(() => setIsRefreshing(false), 1000); // loader for 1s
    }
  };


  // Start and end indices for pagination display
  const startIndex = (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, total);


  return (
    <div className={`w-full ${className} no-scrollbar`}>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden no-scrollbar">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 bg-white">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>{title && <h2 className="text-lg font-gilroy-semibold text-gray-800">{title}</h2>}</div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full md:w-auto">
              {/* Search */}
              {onSearchChange && (
                isProductTab ? (
                  /* 🟢 Product tab search */
                  <div className="relative w-full sm:w-auto">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Search size={16} className="text-gray-400" />
                    </div>

                    <input
                      type="text"
                      value={searchValue ?? ""}
                      onChange={(e) => onSearchChange(e.target.value)}
                      className="w-full sm:w-64 pl-10 pr-4 py-2 h-10 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:bg-white"
                      placeholder={searchPlaceholder}
                    />
                  </div>
                ) : (
                  /* 🔵 Default search for all other tabs */
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!isSearchDisabled) {
                        onSearchSubmit?.(searchValue ?? "");
                      }
                    }}
                    className="flex flex-col gap-1 w-full sm:w-auto"
                  >
                    <div className="flex w-full sm:w-80 h-10 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 focus-within:ring-2 focus-within:ring-brand-blue transition">
                      <div className="flex items-center px-3 text-gray-400">
                        <Search size={16} />
                      </div>

                      <input
                        type="text"
                        value={searchValue ?? ""}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={searchPlaceholder}
                        className="flex-1 bg-transparent px-2 text-sm outline-none placeholder-gray-400"
                      />

                      <button
                        type="submit"
                        disabled={isSearchDisabled}
                        className={`px-4 text-sm flex items-center gap-1 transition-all ${isSearchDisabled
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-brand-blue text-white hover:bg-brand-blue/90"
                          }`}
                      >
                        <Search size={14} />
                        <span className="hidden sm:inline">Search</span>
                      </button>
                    </div>

                    {searchError && (
                      <p className="text-xs text-red-500 pl-1">{searchError}</p>
                    )}
                  </form>
                )
              )}

              {/* Page size */}
              <div className="flex items-center space-x-2 text-sm">
                <label htmlFor="page-size" className="text-gray-500 whitespace-nowrap">Show</label>
                <select
                  id="page-size"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="h-10 pl-3 pr-8 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:bg-white"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="text-gray-500 whitespace-nowrap">entries</span>
              </div>

              {/* ✅ Refresh button */}
              {onRefresh && (
                <button
                  onClick={handleRefresh}
                  disabled={refreshDisabled}
                  className={`flex items-center justify-center h-10 w-10 rounded-lg border ${refreshDisabled
                    ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                    : 'text-gray-600 border-gray-200 hover:bg-gray-100'
                    }`}
                  title={
                    refreshDisabled
                      ? 'Please wait 1 min before refreshing again'
                      : 'Refresh table'
                  }
                >
                  {isRefreshing ? (
                    <Loader2 size={18} className="animate-spin text-brand-blue" />
                  ) : (
                    <RefreshCcw size={18} />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-gray-50 text-xs uppercase tracking-wider">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-6 py-3.5 text-gray-600 font-gilroy-semibold border-b border-gray-200"
                      style={{ width: header.getSize() }}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={`flex items-center ${header.column.getCanSort() ? 'cursor-pointer select-none group' : ''
                            }`}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="ml-2 text-gray-400 group-hover:text-gray-600 transition-colors">
                              {header.column.getIsSorted() === 'asc' ? (
                                <ArrowUp size={14} className="text-brand-blue" />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ArrowDown size={14} className="text-brand-blue" />
                              ) : (
                                <ArrowUpDown size={14} />
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={memoizedColumns.length} className="px-6 py-16 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center">
                      <Loader2 size={30} className="animate-spin mb-2 text-brand-blue" />
                      <span className="text-base">Loading data...</span>
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={memoizedColumns.length} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-500">
                      <Filter size={30} className="mb-2 opacity-40" />
                      <span className="text-base font-gilroy-medium">{emptyStateMessage}</span>
                      <p className="text-sm mt-1 text-gray-400">
                        Try adjusting your search or filter to find what you're looking for.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`${enableRowClick ? 'cursor-pointer' : ''
                      } hover:bg-gray-50 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                      }`}
                    onClick={() => enableRowClick && onRowClick && onRowClick(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-3 whitespace-nowrap text-gray-700 overflow-hidden text-ellipsis">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {showPagination && (
          <div className="border-t border-gray-100 px-6 py-4 bg-white">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-600">
                {total > 0
                  ? `Showing ${startIndex} to ${endIndex} of ${total.toLocaleString()} entries`
                  : 'No entries to show'}
              </div>

              <div className="flex items-center">
                <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                  <button
                    onClick={() => onPageChange(1)}
                    disabled={page === 1}
                    className={`p-2 h-10 ${page === 1 ? 'text-gray-300 cursor-not-allowed bg-gray-50' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    title="First Page"
                  >
                    <ChevronsLeft size={16} />
                  </button>
                  <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 1}
                    className={`p-2 h-10 ${page === 1 ? 'text-gray-300 cursor-not-allowed bg-gray-50' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    title="Previous Page"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className="flex items-center h-10 border-l border-r border-gray-200">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      return (
                        <button
                          key={i}
                          onClick={() => onPageChange(pageNum)}
                          className={`w-10 h-10 flex items-center justify-center transition-colors ${page === pageNum
                            ? 'bg-brand-blue text-white font-gilroy-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={page === totalPages}
                    className={`p-2 h-10 ${page === totalPages ? 'text-gray-300 cursor-not-allowed bg-gray-50' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    title="Next Page"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    onClick={() => onPageChange(totalPages)}
                    disabled={page === totalPages}
                    className={`p-2 h-10 ${page === totalPages ? 'text-gray-300 cursor-not-allowed bg-gray-50' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    title="Last Page"
                  >
                    <ChevronsRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommonTable;
